import { Platform } from 'react-native';

import {
  checkIfInLocalDevelopmentMode,
  getEnvVars,
  getLocalDevelopmentHost,
} from '@order/utils';

const REMOTE_ENDPOINT = 'https://gateway-development.sweetgreen.com/graphql';

const { GRAPHQL_ENDPOINT } = getEnvVars();

// Because we are currently only building once in CI (via nx_affected_build.yml),
// there's no easy way to dynamically inject env vars. This is only a concern
// for web since native platform env vars can be set in eas.json via different
// build profiles. As such, this is a rudimentary work-around. We look at
// window.location.origin and set the graphql endpoint accordingly.

export const getGraphQLEndpoint = () => {
  // Default endpoint (for localhost)
  let graphqlEndpoint = getLocalEndpoint() ?? REMOTE_ENDPOINT;

  if (Platform.OS === 'web') {
    const origin = window?.location?.origin || 'http://localhost:19006';

    /** TECH DEBT
     * We have to support URLs for internal testing against
     * replatforming.sweetgreen.com and then also for external
     * users at order.sweetgreen.com
     *
     * Once we are at 100% rollout to the replatforming website,
     * the references to replatforming should be removed.
     */

    // development + ephemerals
    if (
      origin.includes('replatforming-development') ||
      origin.includes('order-development')
    ) {
      graphqlEndpoint = 'https://order-development.sweetgreen.com/graphql';
    }

    // staging + ephemerals
    if (
      origin.includes('replatforming-staging') ||
      origin.includes('order-staging')
    ) {
      graphqlEndpoint = 'https://order-staging.sweetgreen.com/graphql';
    }

    // production
    if (
      origin === 'https://replatforming.sweetgreen.com' ||
      origin === 'https://order.sweetgreen.com'
    ) {
      graphqlEndpoint = 'https://order.sweetgreen.com/graphql';
    }
  }

  return graphqlEndpoint;
};

// For Expo to connect to local graphql, it needs to use the dynamic debugger host.
const getLocalEndpoint = (): string | undefined => {
  const endpoint = GRAPHQL_ENDPOINT as string | undefined;

  if (!endpoint) return undefined;

  const isLocalHost = endpoint?.includes('localhost');
  const isNativePlatform = Platform.OS !== 'web';
  const isDevEnvironment = checkIfInLocalDevelopmentMode();
  const isDeveloperDevice = isNativePlatform && isLocalHost && isDevEnvironment;

  if (isDeveloperDevice) {
    const host = getLocalDevelopmentHost();
    const port = endpoint?.split('localhost:')?.[1]?.split('/')?.[0] ?? '3000';

    return `http://${host}:${port}/graphql`;
  }

  return endpoint;
};
